/**
 * Contexto de Autenticação
 * Gerencia estado de autenticação e usuário logado
 * 
 * @author Equipe GestaoeCRM
 * @date 25/09/2025
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/services/api';

// Tipos
interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'GESTOR' | 'ANALISTA' | 'SUPER_ADMIN';
  active: boolean;
  tenantId?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

// Contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider
interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // console.log('🔍 DEBUG: AuthProvider inicializado');
  
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // Verificar token ao carregar
  useEffect(() => {
    // console.log('🔍 DEBUG: AuthProvider useEffect executado');
    const token = localStorage.getItem('accessToken');
    // console.log('🔍 DEBUG: Token encontrado:', !!token);
    if (token) {
      // Verificar se token é válido fazendo uma requisição
      checkAuthStatus();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      // console.log('🔍 DEBUG: Verificando status de autenticação...');
      const response = await authApi.getMe();
      // console.log('🔍 DEBUG: Resposta da API:', response);
      if (response.data.success) {
        const userData = response.data.data.user;
        
        // Extrair tenantId do token JWT se não estiver na resposta
        const token = localStorage.getItem('accessToken');
        let tenantId = userData.tenantId;
        
        if (!tenantId && token) {
          try {
            // Decodificar JWT para extrair tenantId
            const payload = JSON.parse(atob(token.split('.')[1]));
            tenantId = payload.tenantId;
          } catch (jwtError) {
            // console.warn('Erro ao decodificar JWT:', jwtError);
          }
        }
        
        // Adicionar tenantId ao objeto user
        setUser({
          ...userData,
          tenantId: tenantId
        });
      } else {
        // Token inválido, limpar storage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
    } catch (error) {
      // Token inválido ou erro, limpar storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login(email, password);
      
      if (response.data.success) {
        const { user: userData, tokens } = response.data.data;
        
        // Salvar tokens
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);
        
        // Extrair tenantId do token JWT
        let tenantId = userData.tenantId;
        if (!tenantId) {
          try {
            const payload = JSON.parse(atob(tokens.accessToken.split('.')[1]));
            tenantId = payload.tenantId;
          } catch (jwtError) {
            // console.warn('Erro ao decodificar JWT no login:', jwtError);
          }
        }
        
        // Definir usuário com tenantId
        setUser({
          ...userData,
          tenantId: tenantId
        });
        
        // Configurar axios para usar o token
        authApi.setAuthToken(tokens.accessToken);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao fazer login');
    }
  };

  const logout = async () => {
    try {
      // Tentar fazer logout no servidor
      await authApi.logout();
    } catch (error) {
      // Ignorar erros de logout no servidor
    } finally {
      // Limpar dados locais
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      
      // Limpar cache do React Query
      queryClient.clear();
      
      // Remover token do axios
      authApi.setAuthToken(null);
    }
  };

  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refreshToken');
      if (!refreshTokenValue) {
        throw new Error('No refresh token');
      }

      const response = await authApi.refresh(refreshTokenValue);
      
      if (response.data.success) {
        const { accessToken } = response.data.data;
        
        // Salvar novo token
        localStorage.setItem('accessToken', accessToken);
        
        // Configurar axios
        authApi.setAuthToken(accessToken);
        
        return accessToken;
      }
    } catch (error) {
      // Refresh token inválido, fazer logout
      await logout();
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook para usar o contexto
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
